import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { injectClientGAData } from "@app/helpers/clientMetadata";
import { RouteFactory } from "@app/routePaths";
import logoBTGLightLg from "@assets/logo-btg-light-lg.svg";
import { BlockTitle } from "@blocks/Blocks";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import { getVisitorCat } from "@components/GAData/helper";
import Header from "@components/Header/Header";
import HomepageMasthead from "@components/Header/HomepageMasthead";
import MyBTCarousel from "@features/mybt/components/Carousel/MyBTCarousel";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import { useBTGHomepageRedirect } from "@hooks/useBTGHomepageRedirect";
import { useWindowSize } from "@hooks/useWindowSize";
import TemplateA from "@particles/card/templates/TemplateA/TemplateA";
import TemplateB from "@particles/card/templates/TemplateB/TemplateB";
import TemplateC from "@particles/card/templates/TemplateC/TemplateC";
import TemplateD from "@particles/card/templates/TemplateD/TemplateD";
import TemplateE from "@particles/card/templates/TemplateE/TemplateE";
import TemplateF from "@particles/card/templates/TemplateF/TemplateF";
import ASEANNewsletter from "@routes/Global/components/ASEANNewsletter";
import BTGlobalNewsletter from "@routes/Global/components/BTGlobalNewsletter";
import ByCountry from "@routes/Global/components/ByCountry";
import {
  BTG_HOMEPAGE_GA_DATA,
  GLOBAL_NAVIGATION,
} from "@routes/Global/utils/constants";
import { GlobalPageData } from "@routes/Global/utils/types";
import { cn } from "@util/helpers";

/**
 * GlobalPageContent component renders the main content for the Global page.
 * It includes sections for top stories, skybox, economy policy, ASEAN business,
 * companies, tech, and a footer with SEO data.
 * Figma: https://www.figma.com/design/rR8akVWS3SksIyaOSqetu4/BAU--WEB-?node-id=11666-5494&m=dev
 */
const GlobalPageContent = ({
  topStoriesProps,
  singaporeProps,
  leadershipProps,
  internationalProps,
  btPlusProps,
  featuredProps,
  aseanProps,
  opinionProps,
  myBTRecommendsData,
  seoFooterData,
}: GlobalPageData): React.ReactElement => {
  useBTGHomepageRedirect();

  const { userInfo, visitorId } = useUserInfo();
  const { isScreenLG } = useWindowSize();
  const [isFeaturedStoryVisible, setIsFeaturedStoryVisible] = useState(true);
  const { ref } = useInView({
    threshold: 0.1,
    onChange(inView) {
      if (!isScreenLG) return;

      setIsFeaturedStoryVisible(inView);
    },
  });

  useEffect(() => {
    const visitorcat = getVisitorCat(
      userInfo?.usertype,
      userInfo?.reguserstatus,
      userInfo?.service_type
    );

    injectClientGAData({
      ...BTG_HOMEPAGE_GA_DATA,
      visitorcat,
      at: visitorId,
    });
  }, [userInfo, visitorId]);

  return (
    <div
      className="w-full overflow-x-clip text-gray-850"
      data-testid="global-home-content-component"
    >
      <Header
        isFromHome
        headerData={GLOBAL_NAVIGATION}
        className={cn(
          "border-gray-850 bg-gray-925 py-2.5 transition-colors duration-300 lg:py-0",
          { "border-gray-175 bg-white": !isFeaturedStoryVisible && isScreenLG }
        )}
        textClassName={cn("text-gray-250 lg:text-gray-850", {
          "lg:text-gray-550 lg:hover:text-gray-550 lg:hover:bg-gray-850":
            isFeaturedStoryVisible && isScreenLG,
        })}
        borderClassName={cn({
          "border-gray-850": isFeaturedStoryVisible && isScreenLG,
        })}
        parentCategory={RouteFactory.global.replace("/", "")}
        editionRef="global-hamburger-menu-edition-switch"
        iconsBgClassName={cn({
          "lg:hover:bg-gray-850": isFeaturedStoryVisible && isScreenLG,
        })}
        subscribeClassName={cn({
          "border-orange-300 bg-gray-925 border lg:text-orange-300 hover:bg-orange-300 hover:text-white":
            isFeaturedStoryVisible && isScreenLG,
        })}
        userIconStrokeColor={cn("border-gray-850", {
          "border-gray-175": !isFeaturedStoryVisible && isScreenLG,
        })}
        isDisplayEditionSwitcher
      />

      <HomepageMasthead
        className="bg-gray-925"
        src={logoBTGLightLg}
        imgWidth={475}
        imgHeight={77}
        theme="dark"
        title="The Business Times Global Edition"
        editionRef="global-header-edition-switch"
        isDisplayEditionSwitcher
        isDisplayH1
      />

      <div ref={ref}>
        <TemplateC
          cardProps={[topStoriesProps[0]]}
          className="mb-4 flex w-full items-center justify-center text-pretty bg-gray-925 md:mb-8"
        />
      </div>

      <Container rootClassName="px-5 lg:px-3">
        <TemplateE cardProps={topStoriesProps.slice(1, 6)} className="mb-10" />

        <div className="mb-12 border-t-2 border-blue-100">
          <BTGlobalNewsletter />
        </div>

        <TemplateB
          header={
            <>
              <BlockTitle
                text="BT Plus"
                rootClassName="text-blue-175 font-freightDisplayPro font-normal !text-10xl text-center"
              />
              <p className="mb-8 text-center font-poppins text-base text-blue-160">
                <em>The best insights across SPH Media</em>
              </p>
            </>
          }
          cardProps={btPlusProps}
          className="mb-16 border-t-2 border-blue-100 pt-3"
        />

        <TemplateF
          header={
            <BlockTitle
              text="Opinion"
              rootClassName="text-blue-175 font-freightDisplayPro font-normal !text-10xl text-center mb-8"
            />
          }
          cardProps={opinionProps}
          className="mb-16 border-t-2 border-blue-100 pt-3"
        />

        <TemplateA
          header={
            <BlockTitle
              text="Singapore"
              rootClassName="text-blue-175 font-freightDisplayPro font-normal !text-10xl mb-8 text-center"
            />
          }
          className="mb-12 border-t-2 border-blue-100 pt-3"
          cardProps={singaporeProps}
        />

        <TemplateB
          header={
            <BlockTitle
              text="The Leadership Playbook"
              rootClassName="text-blue-175 font-freightDisplayPro font-normal !text-10xl text-center mb-8"
            />
          }
          cardProps={leadershipProps}
          className="mb-16 border-t-2 border-blue-100 pt-3"
          cardVariantOverride={{
            kicker: {
              color: "text-blue-175",
              extraClass: cn("font-freightSansPro text-base mb-1 md:mb-0"),
            },
            title: {
              color: "text-gray-850",
              size: cn(
                "text-xl font-lct font-semibold -tracking-2% mb-2 md:mb-1"
              ),
            },
            image: {
              position: "",
              width: cn("w-[7.5rem] md:w-full"),
              aspectRatio: "aspect-3x2",
              imageClassName: cn("object-cover"),
              extraClass: cn("order-2 md:order-1"),
              orientation: "landscape",
            },
            content: {
              width: cn("w-[calc(100%-7.5rem)] md:w-full"),
              extraClass: cn("pr-3 md:pt-3 order-1 md:order-2"),
              blurb: {
                extraClass: cn(
                  "text-gray-750 font-lucida text-base font-medium leading-normal -tracking-5% !line-clamp-5 md:!hidden"
                ),
              },
            },
          }}
        />

        <TemplateC
          cardProps={featuredProps}
          className="-mx-5 mb-12 w-[calc(100%+2.5rem)] bg-gray-925 md:mx-0 md:w-full"
        />

        <TemplateD
          header={
            <BlockTitle
              text="Asean"
              rootClassName="text-blue-175 font-freightDisplayPro font-normal !text-10xl text-center col-span-1 lg:col-span-4"
            />
          }
          cardProps={aseanProps}
          className="col-span-3 mb-12 border-t-2 border-blue-100 pt-3"
          rightSidebar={<ByCountry />}
        />

        <div className="mb-12 border-t-2 border-blue-100">
          <ASEANNewsletter />
        </div>

        <TemplateA
          header={
            <BlockTitle
              text="International"
              rootClassName="text-blue-175 font-freightDisplayPro font-normal !text-10xl mb-8 text-center"
            />
          }
          className="mb-16 border-t-2 border-blue-100 pt-3"
          cardProps={internationalProps}
        />
        <>
          {visitorId ? (
            <MyBTCarousel
              myBTObjects={myBTRecommendsData}
              rootClassName="mb-16 border-t-2 border-blue-100 pt-3"
              testId="global-mybt"
              header={
                <BlockTitle
                  text="myBT"
                  rootClassName="text-blue-175 font-freightDisplayPro font-normal !text-10xl text-center"
                />
              }
              arrowWrapperClassName={cn("-top-10")}
              slideWrapperClassName={cn("border-blue-100")}
              dividerClassName={cn("divide-blue-100")}
              iconClassName={cn("text-verticals-btblue")}
              refCode="global-mybt"
              className="mb-16 border-t-2 border-blue-100 pt-3"
            />
          ) : null}
        </>

        <SeoFooter data={seoFooterData} className="px-0" />
      </Container>
    </div>
  );
};

export default GlobalPageContent;
